import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, Plus, Search, X } from "lucide-react";
import { listContacts, upsertContact, PIPELINE_STAGES } from "@/lib/crm.functions";
import { SectionTabs, CRM_TABS } from "@/components/admin/SectionTabs";

export const Route = createFileRoute("/_authenticated/admin/contacts/")({
  component: Contacts,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="text-center py-20">
        <p className="text-red-400 mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
});

const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : "—");

function Contacts() {
  const load = useServerFn(listContacts);
  const upsert = useServerFn(upsertContact);
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [stage, setStage] = useState<string>("");
  const [addOpen, setAddOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["crm", "contacts", { q, stage }],
    queryFn: () => load({ data: { q: q || undefined, stage: stage || undefined } }),
    staleTime: 15_000,
  });

  const create = useMutation({
    mutationFn: (form: any) => upsert({ data: form }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["crm"] }); setAddOpen(false); },
  });

  return (
    <div className="space-y-6">
      <SectionTabs tabs={CRM_TABS} label="CRM" />
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-amber-300 text-[11px] tracking-[0.25em] uppercase font-semibold">CRM</p>
          <h1 className="font-serif text-3xl text-white mt-1">Contacts</h1>
          <p className="text-sm text-zinc-400 mt-2">Unified record of every lead and customer.</p>
        </div>
        <button onClick={() => setAddOpen(true)} className="inline-flex items-center gap-2 bg-amber-300 text-zinc-950 rounded-full px-4 py-2 text-sm font-semibold hover:bg-amber-200">
          <Plus className="size-4" /> New contact
        </button>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="size-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, city…" className="w-full bg-white/[0.03] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:border-amber-300/40 focus:outline-none" />
        </div>
        <select value={stage} onChange={(e) => setStage(e.target.value)} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-200">
          <option value="">All stages</option>
          {PIPELINE_STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        {isLoading ? (
          <div className="grid place-items-center py-20"><Loader2 className="size-6 animate-spin text-amber-300" /></div>
        ) : (data ?? []).length === 0 ? (
          <p className="text-center text-zinc-500 py-16 text-sm">No contacts match your filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Contact</th>
                  <th className="text-left px-4 py-3">City</th>
                  <th className="text-left px-4 py-3">Stage</th>
                  <th className="text-left px-4 py-3">LTV</th>
                  <th className="text-left px-4 py-3">Last active</th>
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((c: any) => (
                  <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <Link to="/admin/contacts/$id" params={{ id: c.id }} className="text-white hover:text-amber-200">{c.name ?? "—"}</Link>
                    </td>
                    <td className="px-4 py-3 text-zinc-400">
                      {c.email && <div>{c.email}</div>}
                      {c.phone && <div className="text-zinc-500 text-xs">{c.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-zinc-400">{c.city ?? "—"}</td>
                    <td className="px-4 py-3">
                      <StageBadge stage={c.effective_stage ?? c.pipeline_stage} />
                      {(c.opps_open_count > 0 || c.opps_won_count > 0) && (
                        <div className="text-[10px] text-zinc-500 mt-1">
                          {c.opps_open_count > 0 && <span>{c.opps_open_count} open</span>}
                          {c.opps_open_count > 0 && c.opps_won_count > 0 && <span> · </span>}
                          {c.opps_won_count > 0 && <span className="text-emerald-400">{c.opps_won_count} won</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-400">
                      ${Number(c.lifetime_value ?? 0).toLocaleString()}
                      {Number(c.opps_open_value ?? 0) > 0 && (
                        <div className="text-[10px] text-zinc-500">
                          ${Math.round(Number(c.opps_open_value)).toLocaleString()} open
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-500">{fmtDate(c.last_activity_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {addOpen && (
        <NewContactModal
          onClose={() => setAddOpen(false)}
          onSubmit={(f) => create.mutate(f)}
          pending={create.isPending}
          error={create.error?.message}
        />
      )}
    </div>
  );
}

function StageBadge({ stage }: { stage: string }) {
  const map: Record<string, string> = {
    new: "bg-sky-500/20 text-sky-900 dark:text-sky-200 border-sky-500/40",
    contacted: "bg-amber-500/20 text-amber-900 dark:text-amber-200 border-amber-500/40",
    estimate: "bg-violet-500/20 text-violet-900 dark:text-violet-200 border-violet-500/40",
    proposal: "bg-emerald-500/20 text-emerald-900 dark:text-emerald-200 border-emerald-500/40",
    won: "bg-emerald-600/25 text-emerald-900 dark:text-emerald-200 border-emerald-600/50",
    lost: "bg-zinc-500/20 text-zinc-800 dark:text-zinc-300 border-zinc-500/40",
  };
  const label = PIPELINE_STAGES.find((s) => s.key === stage)?.label ?? stage;
  return <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest border ${map[stage] ?? "border-zinc-400/40 text-zinc-700 dark:text-zinc-300"}`}>{label}</span>;
}

function NewContactModal({ onClose, onSubmit, pending, error }: { onClose: () => void; onSubmit: (f: any) => void; pending: boolean; error?: string }) {
  const [form, setForm] = useState<any>({ name: "", email: "", phone: "", city: "", pipeline_stage: "new", notes: "" });
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center px-4 py-8" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0a0a0a] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl text-white">New contact</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="size-5" /></button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}
          className="space-y-3"
        >
          {["name", "email", "phone", "city"].map((k) => (
            <input key={k} placeholder={k[0].toUpperCase() + k.slice(1)} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              type={k === "email" ? "email" : "text"}
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:border-amber-300/40 focus:outline-none" />
          ))}
          <select value={form.pipeline_stage} onChange={(e) => setForm({ ...form, pipeline_stage: e.target.value })}
            className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-200">
            {PIPELINE_STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <textarea placeholder="Notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500" />
          {error && <p className="text-red-400 text-xs">{error}</p>}
          <button disabled={pending} className="w-full bg-amber-300 text-zinc-950 rounded-full py-2.5 text-sm font-semibold disabled:opacity-60">
            {pending ? "Saving…" : "Create contact"}
          </button>
        </form>
      </div>
    </div>
  );
}